/**
 * GOLDEN SLICE (ARCH-7): the coach exercise library's ONLY door to the server.
 *
 * Conventions this module demonstrates (copy them for new domains):
 * - Every call goes through `apiClient` to a Worker endpoint (AGENTS.md §8). No direct
 *   Supabase, no raw fetch.
 * - Non-throwing: each function resolves to the apiClient envelope (`ok`, `status`, `code`,
 *   `body`, …). Callers branch on `ok`, or use `throwIfNotOk()` when they want a throw.
 * - Rows cross the wire in DB (snake_case) shape and are mapped to the camelCase `Exercise`
 *   exactly once, here, by `mapExerciseRow`. Nothing above this layer sees snake_case.
 * - Request bodies are typed by the server's own zod schemas via `@schemas`.
 *
 * Endpoints: functions/api/coach/exercises.ts and functions/api/coach/exercises/* (see _shared.ts).
 */

import { api, type ApiResponse } from '@/lib/apiClient';
import type { SaveExerciseBody } from '@schemas/exercises';
import type { Exercise } from '@/types/exercises';

// =============================================================================
// Wire shape + mapping
// =============================================================================

/** An `exercises` row as the Worker returns it. List responses omit the detail-only columns. */
export interface ExerciseRow {
  id: string;
  owner_coach_id: string | null;
  org_id?: string | null;
  slug?: string | null;
  name: string;
  aliases?: string[] | null;
  description?: string | null;
  coaching_cues?: string | null;
  contraindications?: string | null;
  video_url?: string | null;
  image_urls?: string[] | null;
  modality?: Exercise['modality'];
  primary_muscles?: string[] | null;
  secondary_muscles?: string[] | null;
  body_regions?: string[] | null;
  equipment?: string[] | null;
  mechanics?: string | null;
  movement_patterns?: string[] | null;
  unilateral?: boolean | null;
  track_by?: Exercise['trackBy'];
  default_sets?: number | null;
  default_reps?: string | null;
  default_duration_sec?: number | null;
  default_distance?: number | null;
  default_distance_unit?: Exercise['defaultDistanceUnit'];
  default_interval_type?: Exercise['defaultIntervalType'];
  default_rounds?: number | null;
  default_work_sec?: number | null;
  default_rest_sec?: number | null;
  default_tempo?: string | null;
  default_effort?: string | null;
  default_rest_between_sets_sec?: number | null;
  default_intensity?: string | null;
  default_target_pace_sec?: number | null;
  default_load_kg?: number | null;
  default_load_pct_1rm?: number | null;
  default_incline_pct?: number | null;
  default_speed_kph?: number | null;
  default_cadence_rpm?: number | null;
  variation_of_exercise_id?: string | null;
  tags?: string[] | null;
  is_public?: boolean | null;
  source?: string | null;
  external_id?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

export function mapExerciseRow(r: ExerciseRow): Exercise {
  return {
    id: r.id,
    ownerCoachId: r.owner_coach_id ?? null,
    orgId: r.org_id ?? null,
    slug: r.slug ?? null,

    name: r.name,
    aliases: r.aliases ?? null,
    description: r.description ?? null,
    coachingCues: r.coaching_cues ?? null,
    contraindications: r.contraindications ?? null,

    videoUrl: r.video_url ?? null,
    imageUrls: r.image_urls ?? null,

    modality: r.modality ?? null,
    primaryMuscles: r.primary_muscles ?? null,
    secondaryMuscles: r.secondary_muscles ?? null,
    bodyRegions: r.body_regions ?? null,
    equipment: r.equipment ?? null,
    mechanics: r.mechanics ?? null,
    movementPatterns: r.movement_patterns ?? null,
    unilateral: r.unilateral ?? null,

    trackBy: r.track_by ?? null,
    defaultSets: r.default_sets ?? null,
    defaultReps: r.default_reps ?? null,
    defaultDurationSec: r.default_duration_sec ?? null,
    defaultDistance: r.default_distance ?? null,
    defaultDistanceUnit: r.default_distance_unit ?? null,
    defaultIntervalType: r.default_interval_type ?? null,
    defaultRounds: r.default_rounds ?? null,
    defaultWorkSec: r.default_work_sec ?? null,
    defaultRestSec: r.default_rest_sec ?? null,
    defaultTempo: r.default_tempo ?? null,
    defaultEffort: r.default_effort ?? null,
    defaultRestBetweenSetsSec: r.default_rest_between_sets_sec ?? null,
    defaultIntensity: r.default_intensity ?? null,
    defaultTargetPaceSec: r.default_target_pace_sec ?? null,
    defaultLoadKg: r.default_load_kg ?? null,
    defaultLoadPct1rm: r.default_load_pct_1rm ?? null,
    defaultInclinePct: r.default_incline_pct ?? null,
    defaultSpeedKph: r.default_speed_kph ?? null,
    defaultCadenceRpm: r.default_cadence_rpm ?? null,

    variationOfExerciseId: r.variation_of_exercise_id ?? null,
    tags: r.tags ?? null,
    isPublic: !!r.is_public,

    source: r.source ?? null,
    externalId: r.external_id ?? null,

    createdAt: r.created_at ?? undefined,
    updatedAt: r.updated_at ?? undefined,
    deletedAt: r.deleted_at ?? null,
  };
}

// =============================================================================
// Result types: the apiClient envelope plus the mapped payload on success
// =============================================================================

export type ExerciseResult = ApiResponse<{ exercise?: ExerciseRow }> & { exercise?: Exercise };
export type ExerciseListResult = ApiResponse<{ items?: ExerciseRow[] }> & { exercises?: Exercise[] };
export type SaveExerciseResult = ApiResponse<{ exercise?: ExerciseRow; deduplicated?: boolean }> & {
  exercise?: Exercise;
  deduplicated?: boolean;
};

const exerciseUrl = (id: string, suffix = '') => `/api/coach/exercises/${encodeURIComponent(id)}${suffix}`;

function withExercise<T extends { exercise?: ExerciseRow }>(
  res: ApiResponse<T>,
): ApiResponse<T> & { exercise?: Exercise } {
  const row = res.ok ? res.data?.exercise : undefined;
  return row ? { ...res, exercise: mapExerciseRow(row) } : res;
}

// =============================================================================
// Calls
// =============================================================================

export interface ListExercisesOptions {
  search?: string;
  /** Server caps at 2000. */
  limit?: number;
  sort?: 'recent' | 'alphaAsc' | 'alphaDesc';
  /** Default true: include the shared public catalog alongside the coach's own exercises. */
  includePublic?: boolean;
}

/** GET /api/coach/exercises — the coach's library (+ public catalog unless excluded). */
export async function listExercises(opts: ListExercisesOptions = {}): Promise<ExerciseListResult> {
  const params = new URLSearchParams();
  if (opts.search) params.set('search', opts.search);
  params.set('limit', String(opts.limit ?? 2000));
  params.set('sort', opts.sort ?? 'recent');
  if (opts.includePublic === false) params.set('include_public', 'false');

  const res = await api.get<{ items?: ExerciseRow[] }>(`/api/coach/exercises?${params.toString()}`);
  return res.ok ? { ...res, exercises: (res.data?.items ?? []).map(mapExerciseRow) } : res;
}

/** GET /api/coach/exercises/:id — 404 (`ok: false`) when missing or not visible to this coach. */
export async function getExercise(id: string): Promise<ExerciseResult> {
  return withExercise(await api.get<{ exercise?: ExerciseRow }>(exerciseUrl(id)));
}

/**
 * POST /api/coach/exercises/save — create (no `id`) or update. `deduplicated` is true when an
 * identical ExerciseDB import already existed and was returned instead.
 */
export async function saveExercise(exercise: SaveExerciseBody['exercise']): Promise<SaveExerciseResult> {
  const res = withExercise(
    await api.post<{ exercise?: ExerciseRow; deduplicated?: boolean }>('/api/coach/exercises/save', { exercise }),
  );
  return res.ok ? { ...res, deduplicated: !!res.data?.deduplicated } : res;
}

/** DELETE /api/coach/exercises/:id — soft delete by default; `hard` removes the row. */
export function deleteExercise(id: string, opts: { hard?: boolean } = {}): Promise<ApiResponse> {
  return api.delete(exerciseUrl(id, opts.hard ? '?hard=true' : ''));
}

/** POST /api/coach/exercises/:id/duplicate — a private "<name> (Copy N)" in the coach's library. */
export async function duplicateExercise(id: string): Promise<ExerciseResult> {
  return withExercise(await api.post<{ exercise?: ExerciseRow }>(exerciseUrl(id, '/duplicate')));
}
